import { FC, KeyboardEvent, ReactNode, RefObject, useState } from 'react'
import { AutoComplete, PickerHandle } from 'rsuite'
import useTranslation from 'next-translate/useTranslation'
import LegacySearchIcon from '@rsuite/icons/legacy/Search'

import useIsOnMobile from '../../../../services/useIsOnMobile'
import { SearchMenuItemLabel } from './SearchMenuItem'
import { DropdownMenuData } from './SearchDropdownMenu'

import styles from '../../../../styles/Search.module.less'

interface SearchInputProps {
  searchTerm: string
  searchMenuData: DropdownMenuData[]
  isLargeInput: boolean
  collapsed: boolean
  containerRef: RefObject<HTMLDivElement>
  searchInputRef: RefObject<PickerHandle>
  onChange: (val: string) => void
  onKeyUp: (event: KeyboardEvent<Element>) => void
  onClearClick: () => void
  onSearchClick: () => void
  renderMenu: () => ReactNode
}

const SearchInput: FC<SearchInputProps> = ({
  searchTerm,
  searchMenuData,
  isLargeInput,
  collapsed,
  containerRef,
  searchInputRef,
  onChange,
  onKeyUp,
  onClearClick,
  onSearchClick,
  renderMenu,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const { t } = useTranslation('common')
  const isOnMobile = useIsOnMobile()

  // Handle focus event - always open dropdown when focused (unless in large/collapsed mode)
  const handleFocus = () => {
    if (!isLargeInput && !collapsed) {
      setIsDropdownOpen(true)
    }
  }

  const handleBlur = () => {
    setIsDropdownOpen(false)
  }

  // Handle selection - close dropdown when an item is selected
  const handleSelect = () => {
    setIsDropdownOpen(false)
  }

  const handleClearClick = () => {
    onClearClick()
  }

  // Determine the open prop value
  const getOpenProp = () => {
    if (isLargeInput || collapsed) {
      return false
    }
    return isDropdownOpen
  }

  return (
    <div
      className={`${styles['search-input-container']} ${isLargeInput ? styles.large : ''} ${collapsed ? styles.collapsed : ''}`}
      role="presentation"
      onMouseDown={(e) => {
        // Prevent blur when clicking on dropdown menu
        if (e.target !== searchInputRef.current?.root?.getElementsByTagName('input')[0]) {
          e.preventDefault()
        }
      }}
    >
      <AutoComplete
        ref={searchInputRef}
        data={getOpenProp() ? searchMenuData : []} // Will ignore open prop if data and focus
        open={getOpenProp()}
        filterBy={() => true} // Always show all data that is passed
        onChange={onChange}
        onKeyUp={onKeyUp}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onSelect={handleSelect}
        container={containerRef.current || undefined}
        className="input-round"
        menuClassName={styles['auto-complete-menu']}
        autoFocus={isLargeInput}
        placement="bottomStart"
        data-testid="search-input"
        placeholder={isOnMobile ? t('Search for product') : t('Search for product, category or supplier')}
        value={searchTerm}
        renderMenuItem={(label: ReactNode) => (
          <SearchMenuItemLabel label={label} />
        )}
        renderMenu={renderMenu}
      />
      {searchTerm && (
        // Copied markup from rs-picker, inherits styles from global scope
        <div className="rs-picker-toggle-indicator">
          <button
            className="rs-picker-clean"
            type="button"
            tabIndex={-1}
            onClick={handleClearClick}
            data-testid="clear-btn"
          >
            ✕
          </button>
        </div>
      )}
      {!isLargeInput ? (
        <LegacySearchIcon
          className={styles['search-icon']}
          role="button"
          data-testid="search-icon"
          onClick={onSearchClick}
        />
      ) : null}
    </div>
  )
}

export default SearchInput
